/**
 * OrionMessageRenderer — typed renderer for OIP (Orion Interaction Protocol).
 * Renders oip.type: chat -> message from oip.message only; action_required -> ActionCard; error -> ErrorCard.
 * For chat we use only oip.message (not oip.content or oip.text).
 */
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './OrionMessageRenderer.css';

const OIP_TYPES = { CHAT: 'chat', ACTION_REQUIRED: 'action_required', ERROR: 'error' };

function getActionLabel(action, isRtl) {
  if (isRtl && action.labelAr) return action.labelAr;
  return action.label ?? action.id ?? '';
}

/**
 * @param {Object} props
 * @param {Object} props.oip - OIP payload { type, message?, title?, summary?, actions?, meta? }
 * @param {string} [props.language] - 'ar' | 'en'
 * @param {boolean} [props.isRtl]
 * @param {boolean} [props.debug] - If true (and DEV), show raw JSON
 * @param {Function} [props.onApprove] - (executionId: string, decision: 'approve'|'cancel') => Promise<void>
 * @param {boolean} [props.disabled]
 */
function OrionMessageRenderer({ oip, language = 'ar', isRtl = false, debug = false, onApprove, disabled = false }) {
  const [actionLoading, setActionLoading] = useState(null); // 'approve' | 'cancel' | null
  const isAr = language === 'ar';

  if (!oip || typeof oip !== 'object') return null;

  const type = oip.type;
  const showDebug = debug && typeof process !== 'undefined' && process.env?.NODE_ENV === 'development';

  if (type === OIP_TYPES.ERROR) {
    return (
      <div className="orion-oip orion-oip--error" role="alert" dir={isRtl ? 'rtl' : 'ltr'}>
        <div className="orion-oip-error__message">{oip.message ?? (isAr ? 'حدث خطأ' : 'An error occurred')}</div>
        {oip.code && <div className="orion-oip-error__code">{oip.code}</div>}
        {showDebug && <pre className="orion-oip-debug">{JSON.stringify(oip, null, 2)}</pre>}
      </div>
    );
  }

  if (type === OIP_TYPES.ACTION_REQUIRED) {
    const executionId = oip.meta?.executionId;
    const actions = Array.isArray(oip.actions) ? oip.actions : [];
    const handleAction = async (decision) => {
      if (!executionId || !onApprove || disabled) return;
      setActionLoading(decision);
      try {
        await onApprove(executionId, decision);
      } finally {
        setActionLoading(null);
      }
    };

    return (
      <div className="orion-oip orion-oip--action" role="region" aria-label={oip.title ?? (isAr ? 'إجراء مطلوب' : 'Action required')} dir={isRtl ? 'rtl' : 'ltr'}>
        {oip.title && <div className="orion-oip-action__title">{oip.title}</div>}
        <div className="orion-oip-action__summary">{oip.summary ?? ''}</div>
        <div className="orion-oip-action__actions" role="group">
          {actions.map((action, index) => {
            const id = action.id?.toLowerCase?.() ?? '';
            const isApprove = id === 'approve' || id === 'confirm';
            const isCancel = id === 'cancel';
            const decision = isApprove ? 'approve' : isCancel ? 'cancel' : null;
            const loading = decision && actionLoading === decision;
            const variant = action.variant ?? (isApprove ? 'primary' : isCancel ? 'secondary' : 'secondary');
            const variantClass = variant === 'primary' ? 'orion-oip-action__btn--primary' : variant === 'danger' ? 'orion-oip-action__btn--danger' : 'orion-oip-action__btn--secondary';
            return (
              <button
                key={`${action.id ?? action.label ?? 'action'}-${index}`}
                type="button"
                className={`orion-oip-action__btn ${variantClass}`}
                disabled={disabled || loading}
                onClick={() => decision && handleAction(decision)}
                aria-label={getActionLabel(action, isRtl)}
              >
                {loading ? (isAr ? 'جاري…' : 'Loading…') : getActionLabel(action, isRtl)}
              </button>
            );
          })}
        </div>
        {showDebug && <pre className="orion-oip-debug">{JSON.stringify(oip, null, 2)}</pre>}
      </div>
    );
  }

  if (type === OIP_TYPES.CHAT) {
    // Chat: use only oip.message (never oip.content or oip.text)
    const message = oip.message ?? '';
    return (
      <div className="orion-oip orion-oip--chat" dir={isRtl ? 'rtl' : 'ltr'}>
        <div className="orion-oip-chat__message">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message}</ReactMarkdown>
        </div>
        {showDebug && <pre className="orion-oip-debug">{JSON.stringify(oip, null, 2)}</pre>}
      </div>
    );
  }

  // Unknown OIP type: show ErrorCard
  const unsupportedMessage = isAr ? 'تنسيق الاستجابة غير مدعوم' : 'Unsupported response format';
  return (
    <div className="orion-oip orion-oip--error" role="alert" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="orion-oip-error__message">{unsupportedMessage}</div>
      {showDebug && <pre className="orion-oip-debug">{JSON.stringify(oip, null, 2)}</pre>}
    </div>
  );
}

export default OrionMessageRenderer;
export { OIP_TYPES };
